/* eslint-disable no-param-reassign */
/* global G */
import { asyncpipe, curry, deleteKey, setKey } from 'lib/util'
import { seqModuleAction, seqModuleInit } from 'lib/sequence/app/module'
import seqModuleModel from 'lib/sequence/app/module/model'
import { validate } from 'lib/sequence/app/session/cookie'
import seqSetInitialRoute from 'lib/sequence/app/init/route'
import persistenceInit from 'lib/sequence/app/init/persistence'
import cartInit from 'lib/sequence/app/init/cart'
import settingsInit from 'lib/sequence/app/init/settings'
import pluginsInit from 'lib/sequence/app/init/plugin'
import notificationsInit from 'lib/sequence/app/init/notifications'
import contextInit from 'lib/sequence/app/init/context'
import aclInit from 'lib/sequence/app/acl/init'
import pubSubInit from 'lib/sequence/app/pubsub/connect'
import impersonationInit from 'lib/sequence/app/init/impersonation'
import oauthInit from 'lib/sequence/app/init/oauth'
import initSyncFactoriesFn from 'lib/factory/sync'
import syncLoadersObject from 'lib/loader'
import configProviderFn from 'lib/provider/config'
import contentProviderFn from 'lib/provider/content'
import settings from '@tenant/settings'
import appSettings from 'config/app'
import routeComposition from 'trait/composition/route'
import pinCreatorInit from 'lib/sequence/app/init/pincreator'

/**
 * If the current session's state contains the limit flag, displays a notification informing the
 * user that they should log out before attempting to go to a pre-login route with an active
 * session. It removes the limit flag afterward.
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {function(*): *}
 */
export const showInitializationAlert = obj => (args) => {
  const sessionState = obj[G.SESSION][G.STATE]
  const eventBus = obj[G.ADAPTER][G.EVENTS]

  sessionState[G.LIMIT] && eventBus.dispatch(eventBus.type(G.DATA, G.UNDO), {
    title: obj[G.ADAPTER][G.INTL]._t(
      'dialog.error.context.title',
      {
        ns: 'common',
        _key: 'dialog.error.context.title',
        defaultValue: 'Invalid context',
      },
    ),
    text: obj[G.ADAPTER][G.INTL]._t(
      'dialog.error.context.text',
      {
        ns: 'common',
        _key: 'dialog.error.context.text',
        defaultValue: 'Please, log out first.',
      },
    ),
    children: {
      ok: {
        key: 'ok',
        value: obj[G.ADAPTER][G.INTL]._t(
          'button.ok',
          {
            ns: 'common',
            _key: 'button.ok',
            defaultValue: 'Ok',
          },
        ),
      },
    },
  })

  deleteKey(G.LIMIT, sessionState)

  return args
}

/**
 * Executes Module Init Sequence
 *
 * @param {Gaia.Web.Application} obj - native application
 * @param {*} args - optional arguments
 * @returns {function([*]): *}
 */
const moduleInit = curry(async (obj, route, args) => await seqModuleInit(obj)(route, args))

/**
 * Renders Current Module's View
 *
 * Uses session manager to get current session state
 * Uses platform ui adapter to do initial content render into browser
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {function(*=): *}
 */
const renderModuleInstance = obj => async (args) => {
  const { [G.MODULE]: moduleInstance } = obj[G.SESSION][G.STATE]
  await obj[G.ADAPTER][G.UI][G.API].render(moduleInstance)
  return args
}

/**
 * Sets the application language according to the user's session data.
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {function(*): Promise<*>}
 */
const setSessionLanguage = obj => async (args) => {
  const userSession = obj[G.SESSION][G.STATE][G.META]
  const { lang } = userSession?.refs?.person?.[0]?.value || {}
  lang && await obj[G.ADAPTER][G.INTL].set(lang)
  return args
}

/**
 * Sets the application's title by obtaining the provider and name app translations.
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {(function(*): Promise<*>)|*}
 */
const setApplicationTitle = obj => async (args) => {
  const providerKey = 'app.provider'
  const nameKey = 'app.name'
  const ns = 'custom'

  const providerOptions = { _key: providerKey, ns, defaultValue: 'ApproLogic' }
  const nameOptions = { _key: nameKey, ns, defaultValue: 'Service Pacemaker' }

  const provider = await obj[G.ADAPTER][G.INTL]._t(providerKey, providerOptions)
  const name = await obj[G.ADAPTER][G.INTL]._t(nameKey, nameOptions)

  obj[G.ADAPTER][G.UI][G.API].title(`${provider} | ${name}`)
  return args
}

/**
 * Executes Module ACTION Init Sequence
 *
 * Uses session manager to get current session state
 *
 * @param {Gaia.Web.Application} obj - native application
 * @param {*} args - optional arguments
 * @returns {function([*]): *}
 */
const actionInit = curry(async (obj, route, args) => await seqModuleAction(obj)(route, args))

/**
 * Initializes the application's config and content providers with the platform-dependent adapters
 * set during the bootstrapping phase.
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {function(*): *}
 */
const providersInit = obj => (args) => {
  const factoryProviders = {
    model: obj[G.ADAPTER],
    module: obj[G.ADAPTER],
  }

  /** @type {Gaia.SyncFactories} */
  const factory = initSyncFactoriesFn(factoryProviders)(syncLoadersObject)

  /**
   * @typedef {Object} Gaia.Web.Providers
   * @property {(function(string): (Gaia.AppModule|Gaia.Module))} config - configuration provider
   * @property {Gaia.AppModule} content - content provider
   */
  obj[G.PROVIDER] = {
    config: configProviderFn(factory),
    content: contentProviderFn(factory),
  }

  return args
}

/**
 * Checks whether the user needs onboarding. If the user is logged in already
 * it'll return `false` in any case, because check will be repeated by the
 * `guest/access` action logic after login
 *
 * User needs onboarding if
 * - tenant setting `showOnboardingWarningConfirmation` is set
 * - but `global.onboardingWarningConfirmation.confirmed` is not set in user settings
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {*|boolean|boolean}
 */
export const checkOnboardingConfirmation = (obj) => {
  const sessionState = obj[G.SESSION][G.STATE]

  return sessionState[G.META]
    && settings.showOnboardingWarningConfirmation
    && !sessionState?.[G.SETTINGS]?.global?.onboardingWarningConfirmation?.confirmed
}

/**
 * Initializes the internationalization manager.
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {function(*): Promise<*>}
 */
const intlInit = obj => async (args) => {
  setKey(await obj[G.ADAPTER][G.INTL](), G.INTL, obj[G.ADAPTER])
  return args
}

// todo: asyncPipeSpread() WONT WORK WITH methods wrapped in curry() | asyncCurry(),
//  as it cannot differentiate between curried parameters, and rest spread parameters(...args)
/**
 * Sequence Native Application Initialisation
 *
 * @param {Gaia.Web.Application} obj - native application
 * @returns {function(*=): *}
 */
const sequenceAppInit = obj => asyncpipe(
  intlInit(obj),
  providersInit(obj),
  setApplicationTitle(obj),
  persistenceInit(obj),
  validate(obj), // todo: 401 redirect to login page
  setSessionLanguage(obj),
  settingsInit(obj), // we need this before contextInit, to potentially save
  contextInit(obj),
  seqSetInitialRoute(obj),

  pluginsInit(obj),
  aclInit(obj),
  pubSubInit(obj),

  async (args) => {
    const [route, rest] = args

    // There is a possibility that the user needs to be onboarded first before being
    // allowed to use the application (read {@link checkOnboardingConfirmation})
    // for more). If that's the case, we need to intercept the route the application
    // wants to redirect to and inject `guest/onboarding` (or whatever is defined in
    // app.json5)
    let nextRoute
    let currentRoute
    const shouldOnboard = checkOnboardingConfirmation(obj)
    if (shouldOnboard) {
      const { onboarding } = appSettings.defaults
      nextRoute = route
      currentRoute = routeComposition(onboarding.module, onboarding.action)
    } else {
      currentRoute = route
    }

    await asyncpipe(
      moduleInit(obj, currentRoute[G.MODULE]),
      seqModuleModel(obj, currentRoute[G.REF]),
      renderModuleInstance(obj),
      impersonationInit(obj),
      actionInit(obj, currentRoute[G.ACTION]),
    )(args)

    // If `shouldOnboard` is truthy, that means that the current route is now guest/onboarding
    // After onboarding, we want to redirect to `nextRoute`. Therefore, we'll save it in the action
    // state, and the onboarding process will pick it up and redirect to it after it's done
    shouldOnboard && setKey(nextRoute, 'route', obj[G.SESSION][G.STATE][G.MODULE][G.STATE][G.ACTION][G.STATE])

    return rest
  },
  cartInit(obj),
  // Needs to come after module initialization because we need the {@link NotificationMenu}
  // component to have rendered already, so that emitting the G.INIT (and thus G.DONE afterward)
  // can be listened to by the component
  // All init sequences that communicate with the notifications adapter (like cart, messages)
  // need to come before this init sequence, because they may have already queried the server
  // to fetch their respective resource (e.g. the currently active cart). We'll check for that
  // in the `G.CREATE` method for each group of the notification adapter and can reuse the result
  // of that query and thus don't need to refetch it
  notificationsInit(obj),
  pinCreatorInit(obj),
  showInitializationAlert(obj),
)

export default sequenceAppInit
